import { type FC, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { useBlocNext } from "@blac/react";
import TourGuideCubit from "./TourGuideCubit";
import TourGuideTooltip from "./TourGuideTooltip";
import { Backdrop, HighlightRing, TooltipArrow } from "./tourGuide.styles";
import { TOUR_PORTAL_ID } from "./constants";
import type { TourPlacement } from "./types";
import { DEFAULT_PLACEMENT } from "./types";

const BACKDROP_IN = { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const };
const BACKDROP_OUT = { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] as const };
const SPRING_IN = {
  type: "spring" as const,
  damping: 22,
  stiffness: 340,
  mass: 0.8
};
const QUICK_OUT = { duration: 0.18, ease: [0.4, 0, 1, 1] as const };

const ARROW_ROTATION: Record<TourPlacement, number> = {
  bottom: 0,
  top: 180,
  left: 90,
  right: -90
};

const SLIDE_ENTER: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: 12 },
  top: { y: -12 },
  left: { x: -12 },
  right: { x: 12 }
};

const SLIDE_EXIT: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: 6 },
  top: { y: -6 },
  left: { x: -6 },
  right: { x: 6 }
};

const TourGuideOverlay: FC = () => {
  const [{ activeTourId, steps, currentStepIndex, isTransitioning }, cubit] =
    useBlocNext(TourGuideCubit);

  const portalRef = useRef<HTMLElement | null>(null);

  const handleNext = useCallback((): void => {
    void cubit.next();
  }, [cubit]);

  const handleDismiss = useCallback((): void => {
    void cubit.dismiss();
  }, [cubit]);

  useEffect(() => {
    if (!activeTourId) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        handleDismiss();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeTourId, handleDismiss]);

  if (!portalRef.current) {
    portalRef.current = document.getElementById(TOUR_PORTAL_ID);
  }
  if (!portalRef.current) {
    return null;
  }

  const currentStep =
    activeTourId && steps.length > 0 ? steps[currentStepIndex] : undefined;

  const anchorReady = !!(
    currentStep && cubit.isAnchorRegistered(currentStep.anchorName)
  );

  const showBackdrop = !!(activeTourId && steps.length > 0);
  const showStep = showBackdrop && !isTransitioning && anchorReady;

  const anchorMeta = currentStep
    ? cubit.getAnchorMeta(currentStep.anchorName)
    : undefined;
  const placement = currentStep?.placement ?? DEFAULT_PLACEMENT;
  const rotation = ARROW_ROTATION[placement];

  return createPortal(
    <>
      <AnimatePresence>
        {showBackdrop && (
          <Backdrop
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: BACKDROP_IN }}
            exit={{ opacity: 0, transition: BACKDROP_OUT }}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <HighlightRing
            key={`ring-${currentStepIndex}`}
            anchorName={currentStep.anchorName}
            borderRadius={anchorMeta?.borderRadius}
            onClick={handleNext}
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: SPRING_IN
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              transition: QUICK_OUT
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <TooltipArrow
            key={`arrow-${currentStepIndex}`}
            anchorName={currentStep.anchorName}
            placement={placement}
            initial={{
              opacity: 0,
              rotate: rotation,
              ...SLIDE_ENTER[placement]
            }}
            animate={{
              opacity: 1,
              rotate: rotation,
              x: 0,
              y: 0,
              transition: SPRING_IN
            }}
            exit={{
              opacity: 0,
              rotate: rotation,
              ...SLIDE_EXIT[placement],
              transition: QUICK_OUT
            }}
            aria-hidden="true"
          >
            <svg
              width="16"
              height="8"
              viewBox="0 0 16 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.43157 0.603292C8.64672 -0.201178 7.35328 -0.201179 6.56844 0.603291L0 7.33594L16 7.33594L9.43157 0.603292Z"
                fill="currentColor"
              />
            </svg>
          </TooltipArrow>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showStep && currentStep && (
          <TourGuideTooltip
            key={`tooltip-${currentStepIndex}`}
            step={currentStep}
            stepIndex={currentStepIndex}
            totalSteps={steps.length}
            onNext={handleNext}
          />
        )}
      </AnimatePresence>
    </>,
    portalRef.current
  );
};

export default TourGuideOverlay;
